import { DetailsType, FormData } from 'types/application';
import { ROE_FIELD_MAPPINGS, setDeep, getDeep } from './roe.map';
import { formatDateTime } from 'utils/application.helpers';

import { 
    OTHER_SECT_TYPE as SECT_TYPE,
    OTHER_SECT_FILER as SECT_FILER,
    OTHER_SECT_APPLICANT as SECT_APPLICANT,
    OTHER_SECT_ADDRESS as SECT_ADDRESS,
    OTHER_SECT_COMPANY as SECT_COMPANY
} from 'common/application.const';
import { payloadDeleteApplication, formDeleteApplication, formWithdrawApplication, payloadWithdrawApplication } from '../helpers';
import commonPayload from './common';

import { decryptId } from 'common/decrypt';

// Build nationality lookup dynamically from cached raw list (preferred) or dropdown options.
const buildNationalityMap = (rows?: { Code: string; Description: string }[] | any[]): Record<string, string> => {
    if (!Array.isArray(rows)) return {};
    const rowsArray = rows as any[]; // <-- cast to avoid union conflict
    return rowsArray.reduce<Record<string, string>>((acc, row) => {
        const code = (row.Code || row.code || '').toString().trim().toUpperCase();
        const desc = (row.Description || row.description || '').toString().trim();
        if (code) acc[code] = desc || code;
        return acc;
    }, {});
};

type Props = {
    myInfoContent: any;
    uen?: string;
    id?: string;
};

const autoFillData = async ({ myInfoContent, uen, id }: Props) => {
    const decryptedID = await decryptId(id || '', 'decrypt');
    const decryptedUEN = await decryptId(uen || '', 'decrypt');
    const person = myInfoContent?.corppass || myInfoContent || {};
    const entity = myInfoContent?.['basic-profile'] || {};
    const corpUser = person?.['corppass-user'] || {};
    const address = myInfoContent?.address || person?.regadd || {};
    sessionStorage.setItem('entity-name', entity['entity-name']?.value);

    const isFin = ['F', 'G', 'M'].some((pre: string) => person?.uinfin?.value?.startsWith(pre));

    let idType = '';
    if (isFin) idType = 'FIN';
    if (person?.nationality?.code && !isFin) {
        idType = person.nationality.code === 'SG' ? 'NRC' : 'NRP';
    }

    let salutation = '';
    if (person.sex) {
        salutation = person.sex?.code === 'M' ? 'Mr.' : 'Ms.';
    }

    let corpUserID = corpUser['id-type']?.desc;
    if (corpUserID === 'NRIC') corpUserID = 'NRC';

    const generalForm = {
        id: 'general',
        formID: 'general',
        title: 'Applicant/Filer Details',
        hasError: true,
        values: {
            // Ensure applicant-type present
            [`${SECT_TYPE}-applicant-type`]: 'Applicant',
            [`${SECT_FILER}-id-type`]: corpUserID || idType,
            [`${SECT_FILER}-idno`]: decryptedID,
            [`${SECT_FILER}-salutaion`]: salutation,
            [`${SECT_FILER}-name`]: corpUser.name?.value || person?.name?.value,
            [`${SECT_FILER}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_FILER}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_FILER}-office-no-country-code`]: '65',
            [`${SECT_FILER}-office-no-main`]: '',
            [`${SECT_FILER}-email`]: person?.email?.value,
            [`${SECT_APPLICANT}-id-type`]: corpUserID || idType,
            [`${SECT_APPLICANT}-idno`]: decryptedID,
            [`${SECT_APPLICANT}-salutaion`]: salutation,
            [`${SECT_APPLICANT}-name`]: corpUser.name?.value || person?.name?.value,
            [`${SECT_APPLICANT}-nationality`]: person?.nationality?.code,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_APPLICANT}-email`]: person?.email?.value,
            // Company fields for Corppass
            [`${SECT_COMPANY}-uen-no`]: decryptedUEN,
            [`${SECT_COMPANY}-entity-type`]: entity['entity-type']?.code || entity['entity-type']?.value || '',
            [`${SECT_COMPANY}-company`]: entity['entity-name']?.value,
            [`${SECT_COMPANY}-office-no-country-code`]: '65',
            [`${SECT_COMPANY}-office-no-main`]: '',
            [`${SECT_COMPANY}-address-type`]: address.type || address.addresstype || 'SG',
            [`${SECT_COMPANY}-postal`]: address.postal?.value,
            [`${SECT_COMPANY}-block-house`]: address.block?.value,
            [`${SECT_COMPANY}-street`]: address.street?.value,
            [`${SECT_COMPANY}-floor`]: address.floor?.value,
            [`${SECT_COMPANY}-unit`]: address.unit?.value,
            [`${SECT_COMPANY}-building`]: address.building?.value,
            // Address fields for Singpass
            [`${SECT_ADDRESS}-address-type`]: address.type || address.addresstype || 'SG',
            [`${SECT_ADDRESS}-postal`]: address.postal?.value,
            [`${SECT_ADDRESS}-block-house`]: address.block?.value,
            [`${SECT_ADDRESS}-street`]: address.street?.value,
            [`${SECT_ADDRESS}-floor`]: address.floor?.value,
            [`${SECT_ADDRESS}-unit`]: address.unit?.value,
            [`${SECT_ADDRESS}-building`]: address.building?.value
        }
    };

    return generalForm;
};


const formatRetrieve = async (
    content: any,
    action?: string,
    nationalityData?: { Code: string; Description: string }[],
    dropdownOpts?: any,
    application?: string,
    caseTypeID?: string
) => {
    if (action === 'application-delete') return formDeleteApplication;
    if (action === 'application-withdraw') return formWithdrawApplication;
    
    // Handle download action - return application download files
    if (action === 'download') {
        return await commonPayload.getApplicationDownload(caseTypeID, content?.licenceName || 'Radio Operator Exam');
    }
    
    // New contract: ignore legacy root Filer/Applicant nodes; rely on LICApplication subtree.
    const licApp = content.LICApplication || {};
    const licApplicant = licApp.applicant || {};
    const licFiler = licApp.filer || {};
    const licCompany = licApp.company || {};
    const licCompanyAddr = licCompany.registeredAddress || {};
    const profile = licApp.profile || {};
    const appliedAs = profile.applyAs === 'On behalf of applicant' ? 'Filer' : 'Applicant';

    // Addresses (flattened) already stored directly in applicant/filer.address
    const applicantAddress = licApplicant.address || {};
    // (We currently do not store a separate filer address from form; left blank earlier.)

    // Exam Details (now including exam results dataset)
    const examDetails = licApp.licence?.ExamDetails || {};

    const nationalityMap = buildNationalityMap(
        nationalityData || dropdownOpts?.Nationality?.map((o: any) => ({ Code: o.optID, Description: o.optLabel }))
    );

    const generalForm = {
        id: 'general',
        formID: 'general',
        title: 'Applicant/Filer Details',
        hasError: true,
        values: {
            [`${SECT_TYPE}-applicant-type`]: appliedAs,
            // Filer (only if not Applicant)
            [`${SECT_FILER}-id-type`]: appliedAs === 'Filer' ? licFiler.idType : licApplicant.idType,
            [`${SECT_FILER}-idno`]: appliedAs === 'Filer' ? licFiler.idNumber : licApplicant.idNumber,
            [`${SECT_FILER}-salutaion`]: appliedAs === 'Filer' ? licFiler.salutation : licApplicant.salutation,
            [`${SECT_FILER}-name`]: appliedAs === 'Filer' ? licFiler.name : licApplicant.name,
            // Phone (ensure country code + main split; backend may only return unified contactNumber)
            [`${SECT_FILER}-mobile-no-country-code`]: '65',
            [`${SECT_FILER}-mobile-no-main`]: appliedAs === 'Filer' ? (licFiler.contactNumber || licFiler.mobileNumber || '') : (licApplicant.contactNumber || licApplicant.mobileNumber || ''),
            [`${SECT_FILER}-office-no-country-code`]: '65',
            [`${SECT_FILER}-office-no-main`]: appliedAs === 'Filer' ? (licFiler.officeNumber || licFiler.officeTelNumber || '') : (licApplicant.officeNumber || licApplicant.officeTelNumber || ''),
            [`${SECT_FILER}-email`]: appliedAs === 'Filer' ? licFiler.email : licApplicant.email,
            // Applicant
            [`${SECT_APPLICANT}-id-type`]: licApplicant.idType,
            [`${SECT_APPLICANT}-idno`]: licApplicant.idNumber,
            [`${SECT_APPLICANT}-salutaion`]: licApplicant.salutation,
            [`${SECT_APPLICANT}-name`]: licApplicant.name,
            [`${SECT_APPLICANT}-nationality`]: licApplicant.nationality,
            // Pre-resolved label to avoid dependency on dropdown timing in review renderer
            [`${SECT_APPLICANT}-nationality-label`]: nationalityMap[(licApplicant.nationality || '').toUpperCase()] || licApplicant.nationality || '',
            [`${SECT_APPLICANT}-uen-no`]: licApplicant.companyUEN || licCompany.uen,
            [`${SECT_APPLICANT}-company`]: licApplicant.companyName || licCompany.companyName,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: licApplicant.contactNumber || licApplicant.mobileNumber || '',
            [`${SECT_APPLICANT}-email`]: licApplicant.email,
            // Company details for Corppass
            [`${SECT_COMPANY}-uen-no`]: licApplicant.companyUEN || licCompany.uen,
            [`${SECT_COMPANY}-entity-type`]: licCompany.entityType || '',
            [`${SECT_COMPANY}-company`]: licApplicant.companyName || licCompany.companyName,
            [`${SECT_COMPANY}-office-no-country-code`]: '65',
            [`${SECT_COMPANY}-office-no-main`]: licApplicant.officeNumber || licApplicant.officeTelNumber || '',
            [`${SECT_COMPANY}-postal`]: licCompanyAddr.postalCode || licCompanyAddr.postalcode || '',
            [`${SECT_COMPANY}-block-house`]: licCompanyAddr.blockNo || licCompanyAddr.blockno || '',
            [`${SECT_COMPANY}-street`]: licCompanyAddr.streetName || licCompanyAddr.streetname || '',
            [`${SECT_COMPANY}-floor`]: licCompanyAddr.floor || '',
            [`${SECT_COMPANY}-unit`]: licCompanyAddr.unit || '',
            [`${SECT_COMPANY}-building`]: licCompanyAddr.buildingName || licCompanyAddr.buildingname || '',
            // Address for Singpass
            [`${SECT_ADDRESS}-postal`]: applicantAddress.postalCode || licCompanyAddr.postalcode,
            [`${SECT_ADDRESS}-block-house`]: applicantAddress.blockNo || licCompanyAddr.blockno,
            [`${SECT_ADDRESS}-street`]: applicantAddress.streetName || licCompanyAddr.streetname,
            [`${SECT_ADDRESS}-floor`]: applicantAddress.floor || licCompanyAddr.floor,
            [`${SECT_ADDRESS}-unit`]: applicantAddress.unit || licCompanyAddr.unit,
            [`${SECT_ADDRESS}-building`]: applicantAddress.buildingName || licCompanyAddr.buildingname
        }
    };

    // Add placeholder forms so wizard shows all ROE sections defined in INDUSTRIES (contact & application details)
    // Contact & Billing details population (previously placeholder). Map from nested licence nodes if present.
    const contactPerson = licApp.licence?.contactPersonParticulars || {};
    const contactAddress = licApp.licence?.contactPersonAddress || {};
    const billingPerson = licApp.licence?.billingPersonParticulars || {};
    const billingAddress = licApp.licence?.billingAddress || {};
    const contactAddr = contactAddress.address || {};
    const billingAddr = billingAddress.address || {};
    // Helper to normalise backend same-as flags: only return 'Yes' if explicitly 'Yes' or true; else '' so UI shows unselected.
    // Reverted: retain earlier behavior. If backend sends 'Yes'/true -> 'Yes', if 'No'/false -> 'No', else empty string.
    const yesIf = (v: any) => (v === 'Yes' || v === true ? 'Yes' : v === 'No' || v === false ? 'No' : '');
    const sameAsApplicantVal = yesIf(contactPerson.sameAsApplicantParticulars);
    const sameAsCompanyAddrVal = yesIf(contactAddress.sameAsCompanyRegisteredAddress);
    const sameAsContactParticularsVal = yesIf(billingPerson.sameAsContactPerson || billingPerson.sameAsContactPersonParticulars);
    const sameAsContactAddressVal = yesIf(billingAddress.sameAsContactPersonAddress || billingAddress.sameAsMailingAddress);
    const contactBillingForm: FormData = {
        id: 'contact-billing-details',
        formID: 'contact-billing-details',
        title: 'Billing Details',
        hasError: true,
        values: {
            // Section: contact-particulars (sectionID = 'contact-particulars')
            'contact-particulars-same-as-applicant': sameAsApplicantVal,
            'contact-particulars-contactName': contactPerson.name,
            'contact-particulars-designation': contactPerson.designation,
            'contact-particulars-telNumber': contactPerson.officeTelNumber,
            'contact-particulars-mobileNo': contactPerson.mobileNumber,
            'contact-particulars-email': contactPerson.email,
            'contact-particulars-faxNo': contactPerson.faxNumber,
            // Canonical keys expected: `${sectionID}-${fieldId}` e.g. 'contact-particulars-same-as-applicant'
            // Provide raw fallback keys (without section prefix) to support renderer fallback logic
            'same-as-applicant': sameAsApplicantVal,
            'contactName': contactPerson.name,
            'designation': contactPerson.designation,
            'telNumber': contactPerson.officeTelNumber,
            'mobileNo': contactPerson.mobileNumber,
            'email': contactPerson.email,
            'faxNo': contactPerson.faxNumber,
            // Section: contact-address
            'contact-address-same-as-company-address': sameAsCompanyAddrVal,
            'contact-address-postal': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.postalCode || '' : '',
            'contact-address-blockHouse': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.blockNo || '' : '',
            'contact-address-street': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.streetName || '' : '',
            'contact-address-floor': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.floor || '' : '',
            'contact-address-unit': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.unit || '' : '',
            'contact-address-building': sameAsCompanyAddrVal !== 'Yes' ? contactAddr.buildingName || '' : '',
            'same-as-company-address': sameAsCompanyAddrVal,
            // Section: billing-particulars
            'billing-particulars-same-as-contact': sameAsContactParticularsVal,
            'billing-particulars-billingName': billingPerson.name,
            'billing-particulars-designation': billingPerson.designation,
            'billing-particulars-telNumber': billingPerson.officeTelNumber,
            'billing-particulars-mobileNo': billingPerson.mobileNumber,
            'billing-particulars-email': billingPerson.email,
            'billing-particulars-faxNo': billingPerson.faxNumber,
            'same-as-contact': sameAsContactParticularsVal,
            'billingName': billingPerson.name,
            // reuse designation/telNumber/mobileNo/email raw keys already set above if overlapping naming
            // Section: billing-address
            'billing-address-same-as-contact-address': sameAsContactAddressVal,
            'billing-address-postal': sameAsContactAddressVal !== 'Yes' ? billingAddr.postalCode || '' : '',
            'billing-address-blockHouse': sameAsContactAddressVal !== 'Yes' ? billingAddr.blockNo || '' : '',
            'billing-address-street': sameAsContactAddressVal !== 'Yes' ? billingAddr.streetName || '' : '',
            'billing-address-floor': sameAsContactAddressVal !== 'Yes' ? billingAddr.floor || '' : '',
            'billing-address-unit': sameAsContactAddressVal !== 'Yes' ? billingAddr.unit || '' : '',
            'billing-address-building': sameAsContactAddressVal !== 'Yes' ? billingAddr.buildingName || '' : '',
            'same-as-contact-address': sameAsContactAddressVal
        }
    };
    // --- Exam Details (simplified) ---
    const examConductedBy = generalForm.values['applicant-details-company'] || sessionStorage.getItem('entity-name') || '';
    // Normalise exam results from any legacy locations / casing
    const examResultsRaw: any[] = (
        examDetails.ExamResults ||
        examDetails.examResults ||
        licApp.licence?.examResults ||
        []
    );
    const mappedExamResults = (Array.isArray(examResultsRaw) ? examResultsRaw : [])
        .map((r: any) => {
            const rawAttempts = r.NumberofAttempts ?? r.attempts;
            let attemptsVal = '';
            if (rawAttempts !== undefined && rawAttempts !== null && rawAttempts !== '') {
                const num = Number(rawAttempts);
                if (!isNaN(num)) {
                    attemptsVal = num === 1 ? '1st Attempt' : num === 2 ? '2nd Attempt' : String(rawAttempts);
                } else if (typeof rawAttempts === 'string') {
                    // Normalize any legacy labels
                    if (/^1(a)?/i.test(rawAttempts)) attemptsVal = '1st Attempt';
                    else if (/^2(a)?/i.test(rawAttempts)) attemptsVal = '2nd Attempt';
                    else attemptsVal = rawAttempts;
                }
            }
            // Normalize result code to display label for UI: P/F -> Pass/Fail
            const rawRes = (r.Result ?? r.result ?? '').toString().trim();
            let resultLabel = rawRes;
            if (/^p$/i.test(rawRes) || /^pass$/i.test(rawRes)) resultLabel = 'Pass';
            else if (/^f$/i.test(rawRes) || /^fail$/i.test(rawRes)) resultLabel = 'Fail';
            const natCode = (r.Nationality || r.nationality || '').toString().trim();
            return {
                studentName: r.IndividualName || r.studentName || r.name || '',
                idType: r.IDType || r.idType || '',
                idNumber: r.IDNo || r.idNumber || '',
                nationality: natCode,
                nationalityLabel: nationalityMap[natCode.toUpperCase()] || natCode,
                result: resultLabel,
                attempts: attemptsVal
            };
        })
        // Drop rows that are completely empty
        .filter(r => Object.values(r).some(v => v !== '' && v !== undefined && v !== null));

    const applicationDetailsForm: FormData = {
        id: 'application-details',
        formID: 'application-details',
        title: 'Application Details',
        hasError: true,
        values: {
            // Expose backend case identifier for display (analogous to AEC event ref-no mapping)
            'application-details-caseid': content.caseid || content.caseID || licApp.caseid || licApp.caseID || '',
            // New: application number (preferred display identifier per latest requirement)
            'application-details-application-number': content.applicationNumber || licApp.applicationNumber || '',
            // New: licence name (fallback to static if backend omits)
            'application-details-licence-name': licApp.licence?.licenceName || 'Radio Operator Exam',
            // Use prefixed keys to match renderer's `${sectionID}-${field.id}` access pattern
            'exam-details-exam-type': examDetails.ExamType || '',
            'exam-details-exam-date': examDetails.ExamDate || '',
            'exam-details-exam-conducted-by': examConductedBy || '',
            'exam-details-submission-type': (examDetails.SubType === 'ST1' || examDetails.SubType === 'ST2') ? examDetails.SubType : '',
            // Provide both legacy and canonical key names during transition so existing saved forms still populate.
            'exam-results': mappedExamResults,
            'exam-details-exam-results': mappedExamResults
        }
    };

    // Check if this is view mode (when we should show summary)
    // Similar to event payload pattern - if no specific action and content has case data, show summary
    const isViewMode = application === 'IMDA-IRIS-Work-LICApplication-ROE-VIEW' || 
                      (content?.caseID || content?.caseid || content?.id) && !action;
    
    if (isViewMode) {
        const summaryForm = {
            id: 'summary',
            formID: 'summary',
            title: 'Application Summary',
            hasError: false,
            values: {
                // Using summary- prefix like all other payloads for proper field mapping
                'summary-transactionType': content?.transactionType || content?.Type || 'New',
                'summary-application-name': content?.licenceName || content?.pyLabel || 'Radio Operator Exam',
                'summary-application-number': content?.applicationNumber || content?.pyID || '',
                'summary-application-businessStatus': content?.CustomerStatus || content?.BusinessStatus || 'In Progress',
                'summary-application-submission': formatDateTime(content?.pxCreateDateTime || content?.ApplicationDate || '', true)
            }
        };
        
        return [summaryForm, generalForm, contactBillingForm, applicationDetailsForm];
    }

    // For creation mode (ROE), no summary form
    return [generalForm, contactBillingForm, applicationDetailsForm];
};

// Removed generic applyGeneralMapping for ROE to avoid reintroducing legacy root nodes; explicit construction used instead.

const formatSave = async (details: DetailsType, _caseTypeID: string, pageInstructions: any[] = [], _userRole?: string, action?: string) => {
    if (action === 'application-delete') return payloadDeleteApplication;
    if (action === 'application-withdraw') {
        const withdrawForm = details.forms.find((form: FormData) => form.id === 'application-withdraw')?.values || {};
        const withdrawalReason = String(withdrawForm['aecWithdraw-reason'] || '');
        return payloadWithdrawApplication(withdrawalReason, 'ALT2');
    }
    const general = details.forms.find((form: FormData) => form.id === 'general')?.values || {};
    const contactBilling = details.forms.find((form: FormData) => form.id === 'contact-billing-details')?.values || {};
    const appliedAsValue = general[`${SECT_TYPE}-applicant-type`];
    // (Removed unused generalIDno / generalBDay in revised contract)

    // ROE specific: no EventList processing, so no per-event page instructions.
    let pageInstructionsForDelete: any[] = [];

    const baseContent: any = {
        licenceName: 'Radio Operator Exam',
        LicenceCategory: "General Operator's Certificate (GOC)", // static per business sample (adjust if becomes dynamic)
        transactionType: 'New',
        AppSource: 'FEP',
        Type: 'Individual',
        LICApplication: {
            applicant: {},
            filer: {},
            company: { registeredAddress: {} },
            licence: {},
            profile: { applyAs: appliedAsValue === 'Applicant' ? 'Applicant' : 'On behalf of applicant' }
        }
    };

    // Apply only contact & billing mapping into nested licence structure (these mappings target LICApplication.* paths)
    ROE_FIELD_MAPPINGS.filter(m => m.formId === 'contact-billing-details').forEach(m => {
        const value = contactBilling[m.fieldId];
        // Skip undefined/null, but allow empty string and 'No' for same-as radio fields
        if (value === undefined || value === null) return;
        // For same-as fields, always save the value (Yes/No) even if empty
        const isSameAsField = m.fieldId.includes('same-as');
        if (!isSameAsField && value === '') return;
        setDeep(baseContent, m.backendPath, value);
    });

    //

    // Exam details form values (prefer application-details, but fall back to any form containing the keys)
    const applicationDetails = details.forms.find((form: FormData) => form.id === 'application-details')?.values || {};
    const findInAllForms = (candidates: string[]): any => {
        for (const form of details.forms as FormData[]) {
            for (const key of candidates) {
                const v = (form.values as any)?.[key];
                if (v !== undefined && v !== null && v !== '') return v;
            }
        }
        return undefined;
    };
    // Prefer new prefixed keys; fall back to legacy flat keys for backward compatibility
    const examType = applicationDetails['exam-details-exam-type'] || applicationDetails['exam-type'] || findInAllForms(['exam-details-exam-type','exam-type']);
    const examDate = applicationDetails['exam-details-exam-date'] || applicationDetails['exam-date'] || findInAllForms(['exam-details-exam-date','exam-date']);
    const submissionType = applicationDetails['exam-details-submission-type'] || applicationDetails['submission-type'] || findInAllForms(['exam-details-submission-type','submission-type']);
    let examConductedBy = applicationDetails['exam-details-exam-conducted-by'] || applicationDetails['exam-conducted-by'] || findInAllForms(['exam-details-exam-conducted-by','exam-conducted-by']);
    if (!examConductedBy) {
        const fallbackCompany = general[`${SECT_APPLICANT}-company`];
        const fallbackApplicantName = general[`${SECT_APPLICANT}-name`];
        const fallbackFilerName = general[`${SECT_FILER}-name`];
        examConductedBy = fallbackCompany || fallbackApplicantName || fallbackFilerName || 'N/A';
    }

    // Ensure licence + ExamDetails node exist
    baseContent.LICApplication = baseContent.LICApplication || {};
    baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
    const licence = baseContent.LICApplication.licence;

    // Build ExamDetails object following backend sample keys (capitalized) and always include it.
    // Rule: If no exam fields populated (all empty and no results), send empty object {} to signal absence instead of omitting node.
    // Compatibility: Any legacy flat keys (examType, examDate, examConductedBy, submissionType, examResults) are removed below
    // after migration so downstream always relies on ExamDetails. Retrieval still supports legacy keys for already stored payloads.
    const examDetails: any = {};
    if (examType) examDetails.ExamType = examType;
    if (examDate) examDetails.ExamDate = examDate; // Already validated non-future elsewhere
    // No fallback derivation; user enters manually.
    // Always populate ExamConductedBy with a sensible default
    examDetails.ExamConductedBy = examConductedBy;
    if (submissionType === 'ST1' || submissionType === 'ST2') examDetails.SubType = submissionType;
    // Exam Results mapping (manual / bulk). Expect array of row objects from UI under field id 'exam-results'.
    const uiExamResultsRaw = (applicationDetails['exam-details-exam-results'] ?? applicationDetails['exam-results'])
        ?? findInAllForms(['exam-details-exam-results','exam-results']);
    const uiExamResults: any[] = Array.isArray(uiExamResultsRaw) ? uiExamResultsRaw : [];
    const serialisedExamResults = uiExamResults
        .map(r => {
            const at = (r.attempts || '').toString().trim();
            let attemptsCode: string = '';
            if (at) {
                if (/^1/.test(at)) attemptsCode = '1A';
                else if (/^2/.test(at)) attemptsCode = '2A';
            }
            // Map UI result label to backend code P/F
            const resRaw = (r.result || r.Result || '').toString().trim();
            let resultCode = '';
            if (/^p$/i.test(resRaw) || /^pass$/i.test(resRaw)) resultCode = 'P';
            else if (/^f$/i.test(resRaw) || /^fail$/i.test(resRaw)) resultCode = 'F';
            return {
                IndividualName: (r.studentName || r.name || '').trim(),
                IDType: (r.idType || '').trim(),
                IDNo: (r.idNumber || '').trim(),
                nationality: (r.nationality || '').trim(),
                Result: resultCode,
                NumberofAttempts: attemptsCode
            };
        })
        // Keep only rows with at least one core value (name or ID or result)
        .filter(r => [r.IndividualName, r.IDNo, r.Result].some(v => v));
    // Always include ExamResults array in ExamDetails, even if empty
    examDetails.ExamResults = serialisedExamResults;

    licence.ExamDetails = examDetails; // Always include (possibly empty {})

    // Build applicant & filer nodes per required contract structure (simple flattened address/contact)
    const applicantNode: any = {
        idType: general[`${SECT_APPLICANT}-id-type`],
        address: {
            buildingName: general[`${SECT_ADDRESS}-building`],
            streetName: general[`${SECT_ADDRESS}-street`],
            unit: general[`${SECT_ADDRESS}-unit`],
            blockNo: general[`${SECT_ADDRESS}-block-house`],
            postalCode: general[`${SECT_ADDRESS}-postal`],
            floor: general[`${SECT_ADDRESS}-floor`]
        },
        contactNumber: general[`${SECT_APPLICANT}-mobile-no-main`],
        name: general[`${SECT_APPLICANT}-name`],
        salutation: general[`${SECT_APPLICANT}-salutaion`],
        idNumber: general[`${SECT_APPLICANT}-idno`],
        email: general[`${SECT_APPLICANT}-email`]
    };
    const filerNode: any = {
        idType: general[`${SECT_FILER}-id-type`],
        idNumber: general[`${SECT_FILER}-idno`],
        email: general[`${SECT_FILER}-email`],
        name: general[`${SECT_FILER}-name`],
        salutation: general[`${SECT_FILER}-salutaion`],
        contactNumber: general[`${SECT_FILER}-mobile-no-main`],
        address: {
            buildingName: '',
            streetName: '',
            unit: '',
            blockNo: '',
            postalCode: '',
            floor: ''
        }
    };

    baseContent.LICApplication.applicant = applicantNode;
    if (appliedAsValue !== 'Applicant') {
        baseContent.LICApplication.filer = filerNode;
    } else {
        delete baseContent.LICApplication.filer;
    }

    // Determine if user is Singpass (no UEN) or Corppass (has UEN)
    // For Singpass: applicant.address will have data from SECT_ADDRESS
    // For Corppass: company.registeredAddress will have data from SECT_COMPANY
    const hasCompanyData = Boolean(general[`${SECT_COMPANY}-uen-no`] || general[`${SECT_COMPANY}-company`]);
    const isSingpass = !hasCompanyData;
    
    // Add IDNo and IDNoRef based on login type
    if (isSingpass) {
        baseContent.IDNo = general[`${SECT_APPLICANT}-idno`];
        baseContent.IDNoRef = general[`${SECT_APPLICANT}-idno`];
    } else {
        baseContent.IDNo = general[`${SECT_COMPANY}-uen-no`];
        baseContent.IDNoRef = general[`${SECT_COMPANY}-uen-no`];
    }
    
    // Company node mapping - only for Corppass users
    if (hasCompanyData) {
        baseContent.LICApplication.company = {
            registeredAddress: {
                buildingName: general[`${SECT_COMPANY}-building`],
                streetName: general[`${SECT_COMPANY}-street`],
                unit: general[`${SECT_COMPANY}-unit`],
                blockNo: general[`${SECT_COMPANY}-block-house`],
                postalCode: general[`${SECT_COMPANY}-postal`],
                floor: general[`${SECT_COMPANY}-floor`]
            },
            entityType: general[`${SECT_COMPANY}-entity-type`] || 'LC',
            companyName: general[`${SECT_COMPANY}-company`],
            uen: general[`${SECT_COMPANY}-uen-no`]
        };
    }

    // Pruning logic: remove structurally empty nodes (backend accepts creation without them)
    const licNode = baseContent.LICApplication.licence || {};
    const examDet = licNode.ExamDetails || {};
    const hasExamDetailsData = Object.keys(examDet).length > 0;
    const otherLicenceKeys = Object.keys(licNode).filter(k => k !== 'ExamDetails');
    if (!hasExamDetailsData && otherLicenceKeys.length === 0) {
        delete baseContent.LICApplication.licence;
    }
    const comp = baseContent.LICApplication.company;
    const compAddr = comp?.registeredAddress || {};
    const compFields = [comp?.companyName, comp?.uen, comp?.entityType];
    const compAddrValues = [compAddr.buildingname, compAddr.streetname, compAddr.unit, compAddr.blockno, compAddr.postalcode, compAddr.floor];
    if (compFields.every(v => !v) && compAddrValues.every(v => !v)) {
        delete baseContent.LICApplication.company;
    }
    const filer = baseContent.LICApplication.filer;
    if (filer) {
        const filerFields = [filer.idType, filer.idNumber, filer.name, filer.email, filer.contactNumber];
        if (filerFields.every(v => !v)) delete baseContent.LICApplication.filer;
    }

    // Basic derived sync logic: if same-as flags are set, copy values accordingly (frontend may already hide fields but ensure backend consistency)
    if (contactBilling['contact-particulars-same-as-applicant'] === 'Yes') {
        // Mirror applicant to contact person
        baseContent.LICApplication = baseContent.LICApplication || {};
        baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
        const licence = baseContent.LICApplication.licence;
        const applicant = baseContent.LICApplication.applicant || {};
        licence.contactPersonParticulars = {
            ...(licence.contactPersonParticulars || {}),
            sameAsApplicantParticulars: 'Yes',
            name: applicant.name,
            email: applicant.email,
            mobileNumber: applicant.contactNumber,
            officeTelNumber: applicant.OfficeNumber, // Use same contact number for office tel
            salutation: applicant.salutation,
            designation: applicant.designation,
            faxNumber: applicant.faxNumber
        };
    }
    if (contactBilling['billing-particulars-same-as-contact'] === 'Yes') {
        baseContent.LICApplication = baseContent.LICApplication || {};
        baseContent.LICApplication.licence = baseContent.LICApplication.licence || {};
        const licence = baseContent.LICApplication.licence;
        const contactP = licence.contactPersonParticulars || {};
        licence.billingPersonParticulars = {
            ...(licence.billingPersonParticulars || {}),
            sameAsContactPersonParticulars: 'Yes',
            name: contactP.name,
            email: contactP.email,
            mobileNumber: contactP.mobileNumber,
            officeTelNumber: contactP.officeTelNumber,
            salutation: contactP.salutation,
            designation: contactP.designation,
            faxNumber: contactP.faxNumber
        };
    }

    // IMPORTANT: Backend expects canonical LICApplication case type. We still detect ROE via
    // content.LICApplication.general.licenceName === 'Radio Operator Exam' and/or applicationType flag elsewhere.
    return {
        caseTypeID: 'IMDA-IRIS-Work-LICApplication',
        content: baseContent,
    // pageInstructionsForDelete is expected to be empty for ROE; condition retained for future extensibility.
    ...(pageInstructionsForDelete.length > 0 && { pageInstructions: pageInstructionsForDelete }),
        ...(pageInstructions?.length > 0 && { pageInstructions })
    };
};

// ROE does not use performer / event / venue sections; keep minimal emptySet if consumer expects property.
const emptySet = {};

const roePayload = {
    formatRetrieve,
    formatSave,
    emptySet,
    autoFillData
};

export default roePayload;
